<template>
	<v-form v-model="formValid" @submit.prevent="handleForm">
		<v-row>
			<v-col cols="12" md="6">
				<v-row>
					<v-col cols="12" md="9">
						<v-text-field
							v-model="form.name"
							label="Client Name"
							name="name"
							:error="hasErrors(form, 'name')"
							:error-messages="errorMsg(form, 'name')"
							persistent-placeholder
							autofocus
							outlined
							required
							dense />
					</v-col>
					<v-col>
						<v-select
							v-model="form.is_active"
							:items="[{ text: 'Yes', value: true }, { text: 'No', value: false }]"
							:error="hasErrors(form, 'is_active')"
							:error-messages="errorMsg(form, 'is_active')"
							persistent-placeholder
							label="Active"
							outlined
							dense
							required
							eager />
					</v-col>
				</v-row>

				<v-text-field
					v-model="form.address1"
					label="Address"
					name="address1"
					:error="hasErrors(form, 'address1')"
					:error-messages="errorMsg(form, 'address1')"
					persistent-placeholder
					outlined
					required
					dense />

				<v-text-field
					v-model="form.address2"
					label="Address 2"
					name="address2"
					:error="hasErrors(form, 'address2')"
					:error-messages="errorMsg(form, 'address2')"
					persistent-placeholder
					outlined
					dense />

				<v-row>
					<v-col cols="12" md="4">
						<v-text-field
							v-model="form.city"
							label="City"
							name="city"
							:error="hasErrors(form, 'city')"
							:error-messages="errorMsg(form, 'city')"
							persistent-placeholder
							outlined
							required
							dense />
					</v-col>
					<v-col cols="12" md="4">
						<v-autocomplete
							v-model="form.state_id"
							:items="states"
							:error="hasErrors(form, 'state_id')"
							:error-messages="errorMsg(form, 'state_id')"
							persistent-placeholder
							item-text="name"
							item-value="id"
							label="Select State"
							outlined
							dense
							clearable
							required
							eager />
					</v-col>
					<v-col cols="12" md="4">
						<v-text-field
							v-model="form.zipcode"
							label="Zip Code"
							name="zipcode"
							:error="hasErrors(form, 'zipcode')"
							:error-messages="errorMsg(form, 'zipcode')"
							persistent-placeholder
							outlined
							required
							dense />
					</v-col>
				</v-row>
				<v-divider class="mt-0 mb-5" />
				<v-row>
					<v-col cols="12" md="6">
						<v-text-field
							v-model="form.billing_contact_name"
							label="Billing Contact Name"
							name="billing_contact_name"
							:error="hasErrors(form, 'billing_contact_name')"
							:error-messages="errorMsg(form, 'billing_contact_name')"
							persistent-placeholder
							outlined
							required
							dense />
					</v-col>
					<v-col cols="12" md="6">
						<v-text-field
							v-model="form.billing_contact_phone"
							name="billing_contact_phone"
							label="Billing Contact Phone"
							:error="hasErrors(form, 'billing_contact_phone')"
							:error-messages="errorMsg(form, 'billing_contact_phone')"
							persistent-placeholder
							outlined
							dense />
					</v-col>
				</v-row>

				<v-text-field
					v-model="form.billing_contact_email"
					name="billing_contact_email"
					type="email"
					label="Billing Contact Email"
					:error="hasErrors(form, 'billing_contact_email')"
					:error-messages="errorMsg(form, 'billing_contact_email')"
					persistent-placeholder
					outlined
					required
					dense />
			</v-col>
			<v-col cols="12" md="6" style="border-left: 1px solid #eaeaea">
				<h3 class="text-center mb-4 text-uppercase primary--text">Report Pricing Overrides</h3>
				<div v-for="report in reportTypes" :key="`report-type-` + report.id">
					<v-row dense>
						<v-col cols="5" class="text-right pt-2 pr-2 primary--text">
							{{ report.report_type }}:
						</v-col>
						<v-col cols="5">
							<v-text-field
								v-model.number="form.overrides[report.id]"
								:name="'overrides['+report.id+']'"
								prefix="$"
								dense
								class="mt-0 pt-0"
								:hint="'Default Price: $' + report.default_cost"
								type="number"
								step=".01"
								persistent-hint>
							</v-text-field>
						</v-col>
					</v-row>
				</div>
			</v-col>
		</v-row>
		<v-row>
			<v-col>
				<v-divider class="mt-0 mb-5" />
				<v-btn type="submit"
					color="success"
					large
					:loading="form.processing"
					v-text="buttonLabel" />
			</v-col>
		</v-row>
	</v-form>
</template>
<script>
import api from '@/api'
import states from '@/Mixins/states'

export default {
	mixins: [ states ],
	props: {
		client: {
			type: Object,
			default: () => {}
		}
	},
	data () {
		return {
			reportTypes: [],
			formValid: true,
			form: this.$inertia.form({
				name: null,
				address1: null,
				address2: null,
				city: null,
				state_id: null,
				zipcode: null,
				is_active: null,
				billing_contact_name: null,
				billing_contact_phone: null,
				billing_contact_email: null,
				overrides: {}
			})
		}
	},
	computed: {
		buttonLabel () {
			if (this.client) {
				return 'Update Client'
			}

			return 'Create Client'
		}
	},
	mounted () {
		this.getReportTypes()
		this.populateForm()
	},
	methods: {
		getReportTypes () {
			const method = 'GET'
			const data = {}

			const url = this.route('reports.api.types')

			return api({
				method,
				url,
				data
			}).then(response => {
				this.reportTypes = response.data

				for (const reportTypeKey in this.reportTypes) {
					if (!this.reportTypes.hasOwnProperty(reportTypeKey)) continue
					if (!this.reportTypes[reportTypeKey].hasOwnProperty('id')) continue
					const reportTypeId = this.reportTypes[reportTypeKey]['id']

					if (! this.form.overrides.hasOwnProperty(reportTypeId)) {
						this.form.overrides[reportTypeId] = null
					}
				}
			})
		},

		handleForm () {
			if (this.client) {
				this.form.put(this.route('clients.update', this.client), {
					only: ['clients', 'errors', 'flash'],
					preserveScroll: true,
					preserveState: true,
					onSuccess: () => {
						this.$emit('success')
					}
				})

				return
			}

			this.form.post(this.route('clients.store'), {
				only: ['clients', 'errors', 'flash'],
				onSuccess: () => {
					this.$emit('success')
					this.form.reset()
				}
			})
		},

		populateForm () {
			if (this.client) {
				for (const clientKey in this.client) {
					if (!this.client.hasOwnProperty(clientKey)) continue
					if (this.form.hasOwnProperty(clientKey)) {
						this.form[clientKey] = this.client[clientKey]
					}
				}

				// TODO: report pricing data
			} else {
				this.form.is_active = true
			}
		}
	}
}
</script>
